import { useEditorContext } from "@/contexts/EditorContext";
import { cn } from "@/lib/utils";
import { Editor, EditorContent } from "@tiptap/react";
import { HTMLProps, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ScrollArea } from "../ui/scroll-area";

interface RichTextEditorProps extends Omit<
    HTMLProps<HTMLDivElement>,
    "onChange"
> {
    fullScreen?: Boolean;
    isExpanded?: Boolean;
    // Rendered inside the scroll area, below the editor (signature + forwarded
    // thread). Kept out of the editable content on purpose (AB#1746).
    belowEditor?: ReactNode;
}

interface EditorSectionProps extends Omit<
    HTMLProps<HTMLDivElement>,
    "onChange"
> {
    editor: Editor;
    label?: string;
    showLabel?: Boolean;
    className?: string;
}

const EDITOR_CONTENT_CLASSES = [
    "[&_.is-editor-empty]:before:text-text-secondary",
    "h-full max-w-none",
    "cursor-text p-0 py-1",
    "text-start",
    "[&_.ProseMirror]:m-0",
    "[&_.ProseMirror]:h-full",
    "[&_.ProseMirror]:min-h-17",
    "[&_.ProseMirror]:w-full",
    "[&_.ProseMirror]:outline-none",
    "[&_.is-editor-empty]:before:pointer-events-none",
    "[&_.is-editor-empty]:before:absolute",
    "[&_.is-editor-empty]:before:opacity-50",
    "[&_.is-editor-empty]:before:content-[attr(data-placeholder)]",
    "[&_p]:m-0",
    "[&_ul]:list-disc [&_ul]:pl-4 [&_ul]:ml-2",
    "[&_ol]:list-decimal [&_ol]:pl-4 [&_ol]:ml-2",
    // These values re-create the plain browser default (UA stylesheet) look for
    // h1/h2/h3/blockquote that Tailwind's preflight reset strips out of this
    // editor. They intentionally match what src/hooks/mails/useIframeBridge.ts
    // renders for a received mail body (which applies no reset, so it gets that
    // same browser default look) — keep both in sync if either one changes.
    "[&_h1]:text-[2em] [&_h1]:font-bold [&_h1]:my-[0.67em]",
    "[&_h2]:text-[1.5em] [&_h2]:font-bold [&_h2]:my-[0.83em]",
    "[&_h3]:text-[1.17em] [&_h3]:font-bold [&_h3]:my-[1em]",
    "[&_blockquote]:mx-10 [&_blockquote]:my-4",
].join(" ");

const EditorSection = ({
    editor,
    label,
    showLabel,
    className,
    onFocus,
}: EditorSectionProps) => (
    <div className={cn("flex flex-col", className)}>
        <div
            className={cn(
                "h-0 overflow-hidden text-sm font-medium text-muted-foreground transition-all duration-300",
                showLabel && "h-7 px-2 py-1",
            )}
        >
            {label || ""}
        </div>
        <EditorContent
            className={EDITOR_CONTENT_CLASSES}
            editor={editor}
            onFocus={onFocus}
        />
    </div>
);

const RichTextEditor = ({
    className,
    fullScreen = true,
    onFocus,
    isExpanded = false,
    belowEditor,
    ...props
}: RichTextEditorProps) => {
    const { t } = useTranslation();
    const { editor, suggestionEditor, pendingSuggestion } = useEditorContext();

    if (!editor) {
        return null;
    }

    const showSuggestion = Boolean(suggestionEditor && pendingSuggestion);
    const editorWidth = showSuggestion ? "w-1/2" : "w-full";

    return (
        <div
            className={cn(
                "relative flex min-h-16 flex-col transition-all duration-300",
                className,
            )}
            {...props}
        >
            <div className="absolute bottom-0 left-0 right-0 top-0 flex flex-col">
                <ScrollArea
                    className={cn(
                        "flex h-full max-w-full flex-col overflow-y-auto",
                    )}
                    viewportClassName="[&>div]:!block"
                >
                    <div className="flex min-h-full flex-col">
                        <div
                            className={cn(
                                "flex w-full flex-1",
                                isExpanded && !fullScreen && "py-1",
                            )}
                        >
                            {(!showSuggestion || fullScreen) && (
                                <EditorSection
                                    className={cn(
                                        "transition-all duration-300",
                                        editorWidth,
                                    )}
                                    editor={editor}
                                    label={t("richTextEditor.original")}
                                    onFocus={onFocus}
                                    showLabel={showSuggestion && fullScreen}
                                />
                            )}

                            {showSuggestion && (
                                <EditorSection
                                    className={cn(
                                        "transition-all duration-300",
                                        fullScreen
                                            ? "w-1/2 border-l"
                                            : "w-full",
                                    )}
                                    editor={suggestionEditor!}
                                    label={t("richTextEditor.suggested")}
                                    onFocus={onFocus}
                                    showLabel={showSuggestion && fullScreen}
                                />
                            )}
                        </div>

                        {belowEditor && (
                            <div className="shrink-0 px-4">{belowEditor}</div>
                        )}
                    </div>
                </ScrollArea>
            </div>
        </div>
    );
};

export default RichTextEditor;
